/**
 * Rôle d'un appareil dans son commerce (tenant_devices.device_role) : « standard » ou « PDA stock ».
 * Le rôle est lu par RPC (POST) pour ne jamais sortir du cache du service worker.
 */
import { supabase } from '@/integrations/supabase/client';
import { appErrorFromSupabase } from '@/api/errors';
import type { AppError } from '@/api/errors';
import { err, ok } from '@/api/result';
import type { Result } from '@/api/result';

export type DeviceRole = 'standard' | 'pda_stock';

export const parseDeviceRole = (raw: unknown): DeviceRole =>
  raw === 'pda_stock' ? 'pda_stock' : 'standard';

export type TenantDevice = {
  id: string;
  device_id: string;
  device_name: string | null;
  model: string | null;
  app_version: string | null;
  last_heartbeat_at: string | null;
  device_role: DeviceRole;
  /** Dernière page affichée, remontée par le heartbeat. */
  pathname: string | null;
};

export const getMyDeviceRole = async (deviceId: string): Promise<Result<DeviceRole, AppError>> => {
  const { data, error } = await supabase.rpc('my_device_role', { p_device_id: deviceId });
  if (error) return err(appErrorFromSupabase(error, 'devices.getMyDeviceRole'));
  return ok(parseDeviceRole(data));
};

const readPathname = (metadata: unknown): string | null => {
  if (!metadata || typeof metadata !== 'object') return null;
  const value = (metadata as Record<string, unknown>).pathname;
  return typeof value === 'string' ? value : null;
};

export const listTenantDevices = async (
  tenantId: string,
): Promise<Result<TenantDevice[], AppError>> => {
  const { data, error } = await supabase
    .from('tenant_devices')
    .select(
      'id, device_id, device_name, model, app_version, last_heartbeat_at, device_role, metadata',
    )
    .eq('tenant_id', tenantId)
    .order('last_heartbeat_at', { ascending: false, nullsFirst: false });
  if (error) return err(appErrorFromSupabase(error, 'devices.listTenantDevices'));
  return ok(
    (data ?? []).map(({ metadata, device_role, ...device }) => ({
      ...device,
      device_role: parseDeviceRole(device_role),
      pathname: readPathname(metadata),
    })),
  );
};

export const setDeviceRole = async (
  id: string,
  role: DeviceRole,
): Promise<Result<DeviceRole, AppError>> => {
  const { data, error } = await supabase.rpc('set_device_role', { p_id: id, p_role: role });
  if (error) return err(appErrorFromSupabase(error, 'devices.setDeviceRole'));
  return ok(parseDeviceRole(data));
};
